import { normalizeMbi } from '@/utils/mbi';

const MBI_MASK = '****-***-****';

type WregIntakeForm = {
  first: string;
  last: string;
  dob: string;
  gender: string;
  mbi: string;
  email: string;
  phone: string;
  phone2: string;
  tcAccepted: boolean;
};

type WregIntakeTextField = Exclude<keyof WregIntakeForm, 'tcAccepted'>;

const EMPTY_INTAKE: WregIntakeForm = {
  first: '',
  last: '',
  dob: '',
  gender: '',
  mbi: '',
  email: '',
  phone: '',
  phone2: '',
  tcAccepted: false,
};

const formatMbi = (value: string): string => {
  const c = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 11);
  return [c.slice(0, 4), c.slice(4, 7), c.slice(7, 11)].filter(Boolean).join('-');
};

const ISO_DOB = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

const formatDob = (value: string): string => {
  const iso = ISO_DOB.exec(value.trim());
  const source = iso ? `${iso[2].padStart(2, '0')}${iso[3].padStart(2, '0')}${iso[1]}` : value;
  let d = source.replace(/\D/g, '').slice(0, 8);
  if (d.length >= 1 && +d[0] > 1) d = `0${d.slice(0, 7)}`;
  if (d.length >= 2 && +d.slice(0, 2) > 12) d = `12${d.slice(2)}`;
  if (d.length >= 3 && +d[2] > 3) d = `${d.slice(0, 2)}0${d.slice(2, 7)}`;
  if (d.length >= 4 && +d.slice(2, 4) > 31) d = `${d.slice(0, 2)}31${d.slice(4)}`;
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
};

const formatPhone = (digits: string): string => {
  if (!digits) return '';
  let o = `(${digits.slice(0, 3)}`;
  if (digits.length >= 3) o += ') ';
  if (digits.length > 3) o += digits.slice(3, 6);
  if (digits.length >= 6) o += '-';
  if (digits.length > 6) o += digits.slice(6, 10);
  return o;
};

const dobIsOutOfRange = (dob: string): boolean => {
  const digits = dob.replace(/\D/g, '');
  if (digits.length !== 8) return false;
  const year = +digits.slice(4);
  return year < 1900 || year > new Date().getFullYear();
};

const phonesMatch = (f: WregIntakeForm): boolean =>
  f.phone.length === 10 && f.phone2 === f.phone;

const emailIsValid = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const intakeIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(
    f.first &&
      f.last &&
      f.dob.replace(/\D/g, '').length === 8 &&
      !dobIsOutOfRange(f.dob) &&
      f.gender &&
      normalizeMbi(f.mbi).length === 11 &&
      emailIsValid(f.email) &&
      phonesMatch(f) &&
      f.tcAccepted,
  );

export {
  EMPTY_INTAKE,
  MBI_MASK,
  dobIsOutOfRange,
  emailIsValid,
  formatDob,
  formatMbi,
  formatPhone,
  intakeIsComplete,
  phonesMatch,
};
export type { WregIntakeForm, WregIntakeTextField };
